import { useAppDispatch, useAppSelector } from 'hooks/redux';
import React, { useState, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import WarningIcon from '@mui/icons-material/WarningRounded';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { categoriesTypes } from 'app-components/flux/categories';
import { categoriesActions, categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { bindPromiseAction } from 'app-components/flux/core/actionHelpers';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { scheduledTransactionsActions } from 'app-components/flux/scheduled-transactions';
import type { transactionsTypes } from 'app-components/flux/transactions';
import { transactionsActions, transactionsUtils } from 'app-components/flux/transactions';
import { tracker } from 'app-components/utils/core';

import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import StdDialogForm from 'components/Dialogs/StdDialogForm';
import LoadingView from 'components/LoadingView';
import LoadAllTransactionsHelper from 'containers/Settings/SharedComponents/LoadAllTransactionsHelper';

import CategoryField from 'components/ReactFormHook/CategoryField';

import {
  getRulesMatchingPredicate,
  ruleHasCategoryId,
} from 'app-components/flux/transaction-rules/transactionRulesSelectors';
import type { TransactionRuleType } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import { makeStyles } from 'tss-react/mui';
import { getTxnsMatchingPredicate, txnHasCategoryId } from './selectors';
import { getIdAndDescendantIds } from './utils';

// TODO: Handle memorized txns.

const defaultValues = {
  reassignmentCoa: chartOfAccountsTypes.mkChartOfAccount({ type: 'UNCATEGORIZED', id: '0' }),
};

const useStyles = makeStyles()((theme) => ({
  ruleStatus: {
    display: 'flex',
    marginBottom: theme.spacing(0.5),
  },
  txnStatus: {
    color: theme.palette.text.secondary,
    marginBottom: 4,
    marginTop: theme.spacing(3),
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: theme.palette.divider,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  warningIcon: {
    alignSelf: 'center',
    marginRight: theme.spacing(0.5),
    color: theme.palette.warning.main,
    width: 20,
    height: 20,
  },
}));

const DeleteCategoryDialog = (props: { category: categoriesTypes.Category; onClose: () => void }) => {
  const { category, onClose } = props;
  const dispatch = useAppDispatch();
  const { classes } = useStyles();

  const deleteAndReassignCategoryQcs = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'deleteAndReassignCategoryQcs'),
  );
  const categoriesByParentId = useAppSelector(categoriesSelectors.getCategoriesByParentId);
  const childCatIds = categoriesByParentId
    .get(category?.id as string)
    ?.map((cat) => cat.id)
    .toList()
    ?.toArray();

  const categoryIdsToReassign = getIdAndDescendantIds(category, categoriesByParentId);

  const predicateTxnFilter = useCallback(
    (txn: transactionsTypes.TransactionRecord) => txnHasCategoryId(txn, categoryIdsToReassign),
    [categoryIdsToReassign],
  );

  const predicateRuleFilter = useCallback(
    (rule: TransactionRuleType) => ruleHasCategoryId(rule, categoryIdsToReassign),
    [categoryIdsToReassign],
  );

  const [txnsToUpdate, scheduledTxnsToUpdate] = useAppSelector((state) =>
    getTxnsMatchingPredicate(state, predicateTxnFilter),
  );

  const txnRulesToUpdate: TransactionRuleType[] = useAppSelector((state) =>
    getRulesMatchingPredicate(state, predicateRuleFilter),
  );
  const txnRulesCount = txnRulesToUpdate.length;

  const [showSkeletonView, setShowSkeletonView] = useState(false);

  const hasTxnsToUpdate = txnsToUpdate.size > 0 || scheduledTxnsToUpdate.size > 0;
  const hasChildren = categoryIdsToReassign.size > 1;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({ defaultValues });

  // reassign coa (if split, only reassign split-items with matching coa.id in categoryIdsToReassign)
  const reassignCoa = (txn: transactionsTypes.TransactionRecord, newCoa: chartOfAccountsTypes.ChartOfAccount) => {
    if (txn && transactionsUtils.isSplitTxn(txn)) {
      return txn.setIn(
        ['split', 'items'],
        txn.split?.items?.map((item: transactionsTypes.SplitItem) =>
          categoryIdsToReassign.contains(item.coa?.id) ? item.set('coa', newCoa) : item,
        ),
      );
    }
    return txn?.set('coa', newCoa);
  };

  const excludeCatToDelete = (coa: chartOfAccountsTypes.ChartOfAccount) =>
    coa.id !== category?.id && !childCatIds?.includes(coa.id);

  // Create promise-bound action dispatchers
  const deleteAndReassignCategoryAsync = useMemo(
    () => bindPromiseAction(dispatch, categoriesActions.deleteAndReassignCategory),
    [dispatch],
  );
  const deleteCategoryAsync = useMemo(() => bindPromiseAction(dispatch, categoriesActions.deleteCategory), [dispatch]);

  const onSubmit = async (data: any) => {
    setShowSkeletonView(true);
    try {
      if (deleteAndReassignCategoryQcs && category.id) {
        await deleteAndReassignCategoryAsync({ id: category.id, coa: data.reassignmentCoa });
      } else {
        if (hasTxnsToUpdate) {
          const txnsToUpsert = txnsToUpdate.map((txn) => reassignCoa(txn, data.reassignmentCoa));
          const scheduledTxnsToUpsert = scheduledTxnsToUpdate.map((stxn) =>
            stxn.set(
              'transaction',
              reassignCoa(stxn.transaction as transactionsTypes.TransactionRecord, data.reassignmentCoa),
            ),
          );

          if (txnsToUpsert.size > 0) dispatch(transactionsActions.batchTransactions(txnsToUpsert.toArray()));
          if (scheduledTxnsToUpsert.size > 0)
            dispatch(scheduledTransactionsActions.batchScheduledTransactions(scheduledTxnsToUpsert.toArray()));
        }

        await deleteCategoryAsync(category, { undo: { userMessage: 'Category deleted' } });
      }

      tracker.track(tracker.events.categoryDelete, { name: category.name, has_transactions_assigned: hasTxnsToUpdate });
    } finally {
      // Close dialog after delete completes to prevent DataGrid from trying to access the deleted row
      onClose();
    }
  };

  const renderDeleteForm = () => {
    const confirmation = `Are you sure you want to delete the '${category.name}' category${
      hasChildren ? ' and all of its subcategories' : ''
    }?`;

    let txnStatusTxt = '';
    let rulesTxt;
    if (hasTxnsToUpdate) {
      const categoryText = hasChildren ? ' These categories are' : ' This category is';
      const txnText = `${txnsToUpdate.size} transaction${txnsToUpdate.size === 1 ? '' : 's'}`;
      const scheduledTxnText =
        scheduledTxnsToUpdate.size > 0 ? ` and ${scheduledTxnsToUpdate.size} recurring series` : '';
      txnStatusTxt = `${categoryText} assigned to ${txnText}${scheduledTxnText}.`;

      rulesTxt = txnRulesCount
        ? `${txnRulesCount} Rule${txnRulesCount === 1 ? '' : 's'} use this category.`
        : undefined;
    }

    return (
      <StdDialogForm onSubmit={handleSubmit(onSubmit)}>
        {!showSkeletonView && (
          <StdDialogContent
            id='delete_category_dialog_content'
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              maxWidth: `516px`,
              alignSelf: 'center',
            }}
          >
            <Typography>{confirmation}</Typography>
            {hasTxnsToUpdate && (
              <>
                <Box display='flex' flexDirection={'column'}>
                  <Typography variant='captionRegular' className={classes.txnStatus}>
                    {txnStatusTxt}
                  </Typography>
                  <CategoryField
                    control={control}
                    label='Category to reassign to'
                    name='reassignmentCoa'
                    margin='normal'
                    filterFn={excludeCatToDelete}
                  />
                  {rulesTxt && rulesTxt.length > 0 && (
                    <Stack>
                      <div className={classes.divider}></div>
                      <div className={classes.ruleStatus}>
                        <WarningIcon className={classes.warningIcon} />
                        <Typography variant='bodyMedium'>{rulesTxt}</Typography>
                      </div>
                      <Typography variant='bodyRegular' className={classes.ruleStatus}>
                        These rules will inherit the reassigned category above.
                      </Typography>
                      <Typography variant='bodyRegular' className={classes.ruleStatus}>
                        You can edit your rules from Settings &gt; Rules afterwards.
                      </Typography>
                    </Stack>
                  )}
                </Box>
                {!deleteAndReassignCategoryQcs && (
                  <Box marginTop={3}>
                    <LoadAllTransactionsHelper
                      tooltipText={
                        'This will temporarily load all transactions in the app to search and reassign the category.'
                      }
                      initiator={'delete-category'}
                    />
                  </Box>
                )}
              </>
            )}
          </StdDialogContent>
        )}
        {showSkeletonView && (
          <StdDialogContent
            id='delete_category_dialog_loading_view'
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
            }}
          >
            <LoadingView size={100} />
          </StdDialogContent>
        )}
        {!showSkeletonView && (
          <StdDialogActions
            primary={{
              color: 'error',
              disabled: isSubmitting,
              children: hasTxnsToUpdate ? 'Reassign & Delete' : 'Delete',
              variant: 'outlined',
            }}
            secondary={{
              children: 'Cancel',
              onClick: onClose,
              variant: 'outlined',
            }}
          />
        )}
      </StdDialogForm>
    );
  };

  return (
    <StdDialog
      disableBackdropClick
      onClose={onClose}
      open
      showCloseButton
      size={txnRulesCount > 0 ? 'md' : 'sm'}
      title='Delete Category'
    >
      {renderDeleteForm()}
    </StdDialog>
  );
};

export default DeleteCategoryDialog;
